import React, { useCallback, useEffect, useRef, useState } from "react";
import { NotificationContext } from "./NotificationProvider.context";
import {
  IActiveListFilters,
  INotificationPopup,
  NotificationChangeEvent,
  NotificationChangeListener,
} from "./NotificationProvider.types";
import {
  getNotificationStreamUrl,
  INotification,
  INotificationListParams,
  notificationAPI,
} from "../../api/notificationAPI";
import { useAuth } from "../AuthProvider/AuthProvider";
import {
  applyReadState,
  countUnreadAmong,
  dedupePrependNotification,
  matchesActiveFilters,
} from "./NotificationProvider.utils";
import { syncPushSubscription } from "../../helpers/webPush";
import {
  bindNotificationSoundUnlock,
  playNotificationSound,
} from "../../helpers/notificationSound";

// How long to wait for the stream's CONNECTED event before assuming SSE is
// blocked (corporate proxy, etc.) and falling back to polling the badge.
const STREAM_CONNECT_TIMEOUT_MS = 10000;
const POLL_INTERVAL_MS = 60000;
// Live toasts beyond this are dropped oldest-first so a burst can't bury the screen.
const MAX_POPUPS = 3;

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = (
  props,
) => {
  const { user, isLogin, token } = useAuth();

  const [items, setItems] = useState<INotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [streamConnected, setStreamConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [popups, setPopups] = useState<INotificationPopup[]>([]);

  const itemsRef = useRef<INotification[]>([]);
  itemsRef.current = items;
  const activeFiltersRef = useRef<IActiveListFilters | null>(null);
  const listenersRef = useRef(new Set<NotificationChangeListener>());
  const streamRef = useRef<EventSource | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const connectFallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const emit = useCallback((event: NotificationChangeEvent) => {
    listenersRef.current.forEach((listener) => listener(event));
  }, []);

  const subscribe = useCallback((listener: NotificationChangeListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const setActiveFilters = useCallback((filters: IActiveListFilters | null) => {
    activeFiltersRef.current = filters;
  }, []);

  const refreshUnreadCount = useCallback(async () => {
    try {
      const res = await notificationAPI.getUnreadCount();
      setUnreadCount(res.data?.unread_count ?? 0);
    } catch (error) {
      console.error("Failed to load unread notification count:", error);
    }
  }, []);

  const fetchList = useCallback(async (params: INotificationListParams) => {
    setLoading(true);
    try {
      const res = await notificationAPI.getNotifications(params);
      setItems(res.data || []);
      setTotalRecords(res.totalRecords ?? 0);
      if (res.meta && typeof res.meta.unread_count === "number") {
        setUnreadCount(res.meta.unread_count);
      }
    } catch (error) {
      console.error("Failed to load notifications:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(
    async (notification_uuids: string[], knownUnread?: number) => {
      if (!notification_uuids.length) return;

      // Optimistic: flip locally, let NOTIFICATION_COUNT_UPDATED reconcile.
      const delta = knownUnread ?? countUnreadAmong(itemsRef.current, notification_uuids);
      setItems((prev) => applyReadState(prev, notification_uuids));
      setUnreadCount((prev) => Math.max(0, prev - delta));
      emit({ type: "READ", notification_uuids });

      try {
        await notificationAPI.markNotificationRead(notification_uuids);
      } catch (error) {
        console.error("Failed to mark notification(s) read:", error);
      }
    },
    [emit],
  );

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((item) => ({ ...item, is_read: 1 as const })));
    setUnreadCount(0);
    emit({ type: "READ", all: true });
    try {
      await notificationAPI.markAllNotificationRead();
    } catch (error) {
      console.error("Failed to mark all notifications read:", error);
    }
  }, [emit]);

  // Not optimistic — archiving can't be undone from the UI, so rows only
  // leave the list once the server confirms. The badge is left alone: only
  // read rows are archived.
  const clearAllRead = useCallback(async () => {
    const res = await notificationAPI.clearAllRead();
    setItems((prev) => prev.filter((item) => item.is_read !== 1));
    emit({ type: "CLEARED", allRead: true });
    return res.data?.cleared ?? 0;
  }, [emit]);

  const dismissPopup = useCallback((id: string) => {
    setPopups((prev) => prev.filter((popup) => popup.id !== id));
  }, []);

  // ------------------------------------------------------------------
  // On login: unread badge (the stream keeps it fresh after this), and
  // re-register this browser for push if permission was granted before.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!isLogin || !user?.userUuid) {
      setPopups([]);
      return;
    }
    refreshUnreadCount();
    syncPushSubscription(user.userUuid);
    bindNotificationSoundUnlock();
  }, [isLogin, user?.userUuid, refreshUnreadCount]);

  // ------------------------------------------------------------------
  // Live stream — one connection for the whole app, reopened on token
  // change, torn down on logout/unmount. EventSource reconnects itself
  // on drop (server sends `retry: 5000`), so no hand-rolled retry loop.
  // ------------------------------------------------------------------
  useEffect(() => {
    const stopPolling = () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };

    const startPolling = () => {
      if (pollTimerRef.current) return;
      pollTimerRef.current = setInterval(refreshUnreadCount, POLL_INTERVAL_MS);
    };

    if (!isLogin || !token) {
      setStreamConnected(false);
      stopPolling();
      return;
    }

    const stream = new EventSource(getNotificationStreamUrl());
    streamRef.current = stream;

    connectFallbackTimerRef.current = setTimeout(() => {
      startPolling();
    }, STREAM_CONNECT_TIMEOUT_MS);

    stream.addEventListener("CONNECTED", () => {
      setStreamConnected(true);
      if (connectFallbackTimerRef.current) {
        clearTimeout(connectFallbackTimerRef.current);
        connectFallbackTimerRef.current = null;
      }
      stopPolling();
    });

    stream.addEventListener("NOTIFICATION_RECEIVED", (event: MessageEvent) => {
      try {
        const notification: INotification = JSON.parse(event.data);
        // A reconnect can redeliver a row we already hold — don't count or
        // toast it twice.
        const alreadyHeld = itemsRef.current.some(
          (item) => item.notification_uuid === notification.notification_uuid,
        );
        if (alreadyHeld) return;

        if (matchesActiveFilters(notification, activeFiltersRef.current)) {
          setItems((prev) => dedupePrependNotification(prev, notification));
          setTotalRecords((prev) => prev + 1);
        }
        if (notification.is_read !== 1) {
          setUnreadCount((prev) => prev + 1);
          playNotificationSound();
        }
        setPopups((prev) =>
          [
            ...prev.filter(
              (popup) => popup.notification.notification_uuid !== notification.notification_uuid,
            ),
            { id: `${notification.notification_uuid}-${Date.now()}`, notification },
          ].slice(-MAX_POPUPS),
        );
        emit({ type: "RECEIVED", notification });
      } catch (error) {
        console.error("Failed to parse NOTIFICATION_RECEIVED payload:", error);
      }
    });

    stream.addEventListener("NOTIFICATION_COUNT_UPDATED", (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data);
        // Authoritative — always overwrite, never add/subtract. This is
        // what keeps other tabs/devices in sync.
        setUnreadCount(payload.unread_count ?? 0);

        if (payload.all) {
          setItems((prev) => prev.map((item) => ({ ...item, is_read: 1 as const })));
          emit({ type: "READ", all: true });
        } else if (Array.isArray(payload.notification_uuids) && payload.notification_uuids.length) {
          setItems((prev) => applyReadState(prev, payload.notification_uuids));
          emit({ type: "READ", notification_uuids: payload.notification_uuids });
        }
      } catch (error) {
        console.error("Failed to parse NOTIFICATION_COUNT_UPDATED payload:", error);
      }
    });

    // Read rows were archived (usually from another tab). The badge did not
    // change — only drop the rows.
    stream.addEventListener("NOTIFICATION_CLEARED", (event: MessageEvent) => {
      try {
        const payload = JSON.parse(event.data);
        const uuids: string[] = Array.isArray(payload.notification_uuids)
          ? payload.notification_uuids
          : [];
        if (!uuids.length) return;
        const uuidSet = new Set(uuids);
        const removed = itemsRef.current.filter((item) =>
          uuidSet.has(item.notification_uuid),
        ).length;
        setItems((prev) => prev.filter((item) => !uuidSet.has(item.notification_uuid)));
        if (removed) setTotalRecords((prev) => Math.max(0, prev - removed));
        emit({ type: "CLEARED", notification_uuids: uuids });
      } catch (error) {
        console.error("Failed to parse NOTIFICATION_CLEARED payload:", error);
      }
    });

    stream.onerror = () => {
      setStreamConnected(false);
    };

    return () => {
      if (connectFallbackTimerRef.current) {
        clearTimeout(connectFallbackTimerRef.current);
        connectFallbackTimerRef.current = null;
      }
      stopPolling();
      stream.close();
      streamRef.current = null;
      setStreamConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogin, token]);

  return (
    <NotificationContext.Provider
      value={{
        items,
        unreadCount,
        totalRecords,
        streamConnected,
        loading,
        popups,
        fetchList,
        markRead,
        markAllRead,
        clearAllRead,
        refreshUnreadCount,
        setActiveFilters,
        subscribe,
        dismissPopup,
      }}
    >
      {props.children}
    </NotificationContext.Provider>
  );
};
